<template>

  <div ref="rootRef" class="dropdown" :class="{ show: isOpen }">
    <ButtonElement
      size='sm'
      class="btn--ms btn--accent-secondary"
      id="photoUploadButton"
      :aria-expanded="isOpen"
      @click="toggle"
    >
      {{ buttonLabel }}
    </ButtonElement>

    <ul
      class="dropdown-menu"
      :class="{ show: isOpen }"
      id="dropdownPhotoUpload"
      @click.stop=""
    >
      <div class="dropdown-menu-box animation">
        <div class="photo-upload-widget">
          <div class="photo-upload-heading"></div>
          <div class="photo-upload-body">
            <div class="photo-upload-body-div" v-if="!imageUrl && !fileUploaded">
              <div
                @dragover.prevent
                @drop.prevent
                @drop="onFileDrop"
                @dragenter="onDragEnter"
                @dragleave="onDragLeave"
                class="photo-upload-area"
                :class="{ 'drag-state-active': dragEnterOnUploadArea }"
              >
                <input
                  type="file"
                  hidden
                  multiple
                  name="uploadFieldName"
                  @change="onSelectImage($event)"
                  accept="image/*"
                  id="file-upload"
                  class="input-file"
                />
                <div class="photo-upload-label">Drag and drop your image here</div>
              </div>
              <div class="photo-upload-seperator my-3">or</div>
              <div class="photo-upload-footer"
                   :class="{ 'footer-to-right mt-3': fileUploaded }">
                <ButtonElement @click="activateFileUploadWindow" class="btn--ms btn--accent">
                  Browse
                </ButtonElement>
              </div>
            </div>

            <div v-if="!imageUrl && fileUploaded" class="photo-upload-preview">
              <img class="image-preview" id="image-preview" :src="uploadedFileData" alt="" srcset="" />
            </div>
            <div v-if="imageUrl && !fileUploaded" class="photo-upload-preview">
              <img class="image-preview" id="image-preview" :src="imageUrl" alt="" />
            </div>
          </div>
          <div
            v-if="!!imageKey || fileUploaded"
            class="photo-upload-footer px-2"
            :class="{ 'footer-to-right mt-3': fileUploaded }"
          >
            <ButtonElement @click="clearImage" class="btn--ms btn--neutral-secondary">
              Clear
            </ButtonElement>
            <ButtonElement id="btnStartUpload" @click="startUpload" class="btn--ms btn--accent">
              Save
            </ButtonElement>
          </div>
        </div>
      </div>
    </ul>
  </div>

</template>

<script setup lang="ts">
import {ref, watch} from 'vue';
import {CommonNs} from '@shared/types';
import {useDropdown} from '@shared/composables/use-dropdown';
import ButtonElement from '@shared/components/elements/button/button.element.vue';
import {toast} from '@shared/ms-components'

const props = defineProps<{
  buttonLabel: string;
  imageKey: string;
  imageSize: string;
}>();

const emit = defineEmits<{
  (e: 'saveImage', file: CommonNs.ImageData | null): void;
}>();

const {isOpen, rootRef, toggle, close} = useDropdown();

const imageUrl              = ref('');
const selectedFile          = ref<CommonNs.ImageData>({} as CommonNs.ImageData);
const uploadedFileData      = ref('');
const fileUploaded          = ref(false);
const dragEnterOnUploadArea = ref(false);
const imageTypes            = ['image/png', 'image/gif', 'image/bmp', 'image/jpeg'];

watch(() => props.imageKey, (value: string) => {
  imageUrl.value = value ? `${value}/${props.imageSize}` : '';
}, {immediate: true});

function activateFileUploadWindow(): void {
  document.getElementById('file-upload')?.click();
}

function clearImage(): void {
  uploadedFileData.value = '';
  fileUploaded.value     = false;
  selectedFile.value     = {} as CommonNs.ImageData;
  imageUrl.value         = '';
}

function startUpload(): void {
  const img = document.getElementById('image-preview') as HTMLImageElement | null;
  fileUploaded.value     = false;
  uploadedFileData.value = '';
  selectedFile.value     = {} as CommonNs.ImageData;
  if (!img) {
    emit('saveImage', null);
  } else {
    const fileData = dataURIToBlob(img.src);
    selectedFile.value.file = fileData;
    emit('saveImage', selectedFile.value);
  }
  close();
}

function onDragEnter(): void { dragEnterOnUploadArea.value = true; }
function onDragLeave(): void { dragEnterOnUploadArea.value = false; }

function onFileDrop(event: DragEvent): void {
  const files = event.dataTransfer?.files;
  if (files && files.length === 1 && files[0] && imageTypes.includes(files[0].type)) {
    loadImage(files);
  }
}

function onSelectImage(event: Event): void {
  const input = event.target as HTMLInputElement;
  if (input.files && input.files[0]) loadImage(input.files);
}

function loadImage(files: FileList): void {
  const first = files[0];
  if (!first) return;
  if (first.size > 5_242_700) {
    toast.warning('Maximum image size allowed is 5 MB');
    return;
  }

  const reader = new FileReader();
  reader.onload = (e) => {
    uploadedFileData.value = (e.target?.result as string) ?? '';
  };
  reader.readAsDataURL(first);

  selectedFile.value = {name: first.name, type: first.type} as CommonNs.ImageData;
  fileUploaded.value = true;
}

function dataURIToBlob(dataURI: string): Blob {
  const splitDataURI = dataURI.split(',');
  const header = splitDataURI[0] ?? '';
  const payload = splitDataURI[1] ?? '';
  const byteString = header.includes('base64') ? atob(payload) : decodeURI(payload);
  const mimeString = header.split(':')[1]?.split(';')[0] ?? '';
  const ia = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
  return new Blob([ia], {type: mimeString});
}
</script>

<style lang="scss" scoped>
.dropdown-menu {
  width: 300px;
}

.photo-upload-widget {
  .photo-upload-heading {
    font-size: var(--fs-12);
    padding: var(--space-8);
    color: var(--text-neutral);
    text-transform: uppercase;
  }

  .photo-upload-body {
    display: flex;
    position: relative;
    justify-content: center;
    align-items: center;

    .photo-upload-body-div{
      width: 100%;
    }

    .photo-upload-area {
      border-top: 1px dashed var(--border-neutral-dark);
      border-bottom: 1px dashed var(--border-neutral-dark);
      width: 100%;
      height: 180px;
      display: flex;
      justify-content: center;
      align-items: center;

      &.drag-state-active {
        background: var(--neutral-100);
      }

      .photo-upload-label {
        font-size: var(--fs-13);
        text-align: center;
        color: var(--text-neutral);
        width: 60%;
        line-height: 15px;
        pointer-events: none;
      }
    }

    .photo-upload-preview {
      width: 100%;
      height: 180px;
      display: flex;
      justify-content: center;
      align-items: center;
      background: url("../../../../assets/images/upload-dropzone-bg.png");
      background-size: cover;
      background-repeat: no-repeat;
      background-position: center center;
      overflow: hidden;

      .image-preview {
        height: 100%;
        overflow: hidden;
        -webkit-user-drag: none;
        -khtml-user-drag: none;
        -moz-user-drag: none;
        -o-user-drag: none;
      }
    }

    .photo-upload-crosshair {
      position: absolute;
      border: 1px dashed var(--border-accent-dark);
      width: 150px;
      height: 150px;
    }
  }

  .photo-upload-seperator {
    font-size: var(--fs-12);
    text-align: center;
    color: var(--text-neutral);
  }

  .photo-zoom-slider-wrapper {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    margin: var(--space-8) var(--space-0);

    .photo-zoom-slider {
      width: 180px;
      -webkit-appearance: none;
      appearance: none;
      height: 5px;
      border-radius: $br-lg-oval;
      background: var(--neutral-100);
      outline: none;

      &::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        width: 15px;
        height: 15px;
        border-radius: $br-circle;
        opacity: 1;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
        background: var(--light-100);
        cursor: pointer;
      }
    }

    .slider-button {
      padding: var(--space-6) var(--space-12);
      display: flex;
      align-items: center;
      font-size: var(--fs-14);
    }
  }

  .photo-upload-footer {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: var(--space-8);
    gap: var(--space-8);

    &.footer-to-right {
      justify-content: flex-end;
    }
  }
}

</style>
